import * as Sentry from '@sentry/react';

import { createBeforeSend } from './beforeSend';

export const logger = {
  init() {
    const dsn = import.meta.env.VITE_SENTRY_DSN;
    const isProductionBuild = import.meta.env.PROD;

    if (!dsn) {
      return;
    }

    Sentry.init({
      dsn,
      environment: import.meta.env.MODE || 'development',
      release: import.meta.env.VITE_APP_VERSION || 'development-local',
      sendDefaultPii: true,
      attachStacktrace: true,
      beforeSend: createBeforeSend(navigator.userAgent),
      integrations: isProductionBuild
        ? [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
              maskAllText: false,
              blockAllMedia: false,
              // Spend the 50/month replay allowance deliberately. A flat
              // rate across ALL errors spends it on connectivity noise and
              // almost never catches the rare crash you actually need to
              // watch. Gate it on errors that reached the root boundary —
              // keyed on the tag defaultOnCatch sets (main.tsx), NOT on a
              // message: the old synthetic 'Error caused error boundary'
              // wrapper no longer exists, and a message key would silently
              // record nothing.
              beforeErrorSampling: (event) =>
                event.tags?.section === 'error-boundary',
            }),
          ]
        : [],
      tracesSampleRate: isProductionBuild ? 0.1 : 0,
      // Replays have their OWN monthly allowance (50 on the current plan),
      // separate from the error quota. At 0.01 of sessions plus a replay for
      // EVERY error we were structurally over it — the category has sat in
      // usageExceeded, so replays were being discarded anyway.
      //
      // Session sampling is untargeted (it records whoever happens to be
      // picked), so it is the first thing to go. Error-triggered replays are
      // the ones with diagnostic value: at ~370 errors/month projected, 0.05
      // yields ~18/month and stays inside the allowance with headroom.
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: isProductionBuild ? 0.05 : 0,
    });
  },
  captureException: Sentry.captureException,
  captureMessage: Sentry.captureMessage,
  addBreadcrumb: Sentry.addBreadcrumb,
  setUser: Sentry.setUser,
  setContext: Sentry.setContext,
  setTag: Sentry.setTag,
};

export { ErrorHandler, useErrorHandler } from './errorHandler';
